// Mirror of foyr_agent/optimizer/rubric.py. Same axis weights, same
// must_contain / must_not_contain semantics. Kept tiny on purpose so
// the UI can preview the rubric the backend would apply, without a
// round-trip. When the backend lands, the runtime swaps to the real
// API and this file becomes the typecheck-time spec.

import type { DesignerVoice, QuerySample } from "./types";

const VOICE_FINGERPRINTS: Record<DesignerVoice, { preferred: Set<string>; lo: number; hi: number }> = {
  "calm-expert":  { preferred: new Set(["because","noted","ontology","trace","rule"]), lo: 12, hi:  90 },
  "warm-coach":   { preferred: new Set(["sure","great","easy","try","we"]),            lo: 10, hi: 110 },
  "concise-pro":  { preferred: new Set(["action","ok","queued","ack","done"]),          lo:  3, hi:  40 },
};

export interface RubricBreakdown {
  ontology_coverage: number;
  rule_compliance: number;
  voice_match: number;
  brevity: number;
  total: number;
}

function r3(n: number): number { return Math.round(n * 1000) / 1000; }

export function scoreAnswer(args: {
  answer: string;
  sample: QuerySample;
  voice: DesignerVoice;
}): RubricBreakdown {
  const { answer, sample, voice } = args;
  const a = answer.toLowerCase();

  const terms = sample.ontology_terms ?? [];
  const ontology_coverage = terms.length === 0
    ? 2.5
    : r3((2.5 * terms.filter((t) => a.includes(String(t).toLowerCase())).length) / terms.length);

  let rule_compliance = 2.5;
  if ((sample.must_not_contain ?? []).some((f) => a.includes(String(f).toLowerCase()))) {
    rule_compliance = 0;
  } else {
    const req = sample.must_contain ?? [];
    if (req.length > 0) {
      const hits = req.filter((r) => a.includes(String(r).toLowerCase())).length;
      rule_compliance = r3((2.5 * hits) / req.length);
    }
  }

  const fp = VOICE_FINGERPRINTS[voice];
  const words = answer.toLowerCase().split(/\s+/).filter(Boolean);
  const lengthOk = words.length >= fp.lo && words.length <= fp.hi;
  const fpHits = words.filter((w) => fp.preferred.has(w.replace(/[.,;:!?]+$/, ""))).length;
  const voice_match = r3(Math.min(2.5, (lengthOk ? 1.5 : 0.5) + 0.2 * fpHits));

  const overflow = Math.max(0, words.length - fp.hi);
  const brevity = r3(Math.max(0, 2.5 - Math.min(2.5, overflow * 0.05)));

  const total = r3(ontology_coverage + rule_compliance + voice_match + brevity);
  return { ontology_coverage, rule_compliance, voice_match, brevity, total };
}
